import { useEffect, useState } from "react";
import filteringProps from "../filtering/automationrules.json";
import sortingProps from "../sorting/automationrules.json";
import { DataTable, DataTableField } from "../../../../components/data/DataTable";
import DataTableActiveToggle from "../../../../components/data/DataTableActiveToggle";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { API_ROUTES } from "../../../../core/Constants";
import { AutomationActions } from "../../../../core/Enums";
import { setPage, toPrettyDate, useHub } from "../../../../core/Global";
import AutomationRuleItem from "../models/AutomationRuleItem";
import { automationActionLabel, automationEventLabel } from "../models/AutomationLabels";
import AutomationRuleForm from "../components/AutomationRuleForm";


export default function AutomationRulesList() {

  const hub = useHub();

  const [createDrawer, createDrawerSet] = useState<boolean>(false);
  const [editDrawer, editDrawerSet] = useState<boolean>(false);
  const [selectedItem, selectedItemSet] = useState<AutomationRuleItem>();

  const [refresh, refreshSet] = useState<boolean>();

  // The rule rows store ids; these are what turn them back into names. One fetch per
  // list, not per row.
  const [jobStatuses, jobStatusesSet] = useState<any[]>([]);
  const [jobTypes, jobTypesSet] = useState<any[]>([]);
  const [jobSources, jobSourcesSet] = useState<any[]>([]);
  const [techs, techsSet] = useState<any[]>([]);
  const [metros, metrosSet] = useState<any[]>([]);

  useEffect(() => {
    setPage("Automation Rules", "CRM", "CRM Settings");

    hub.Get(API_ROUTES.TagifyJobStatuses, res => jobStatusesSet(res.data ?? []));
    hub.Get(API_ROUTES.TagifyJobTypes, res => jobTypesSet(res.data ?? []));
    hub.Get(API_ROUTES.TagifyJobSources, res => jobSourcesSet(res.data ?? []));
    hub.Get(API_ROUTES.TagifyTechnicians, res => techsSet(res.data ?? []));
    hub.Get(API_ROUTES.TagifyMetros, res => metrosSet(res.data ?? []));
  }, [])

  useEffect(() => {
    if (refresh) setTimeout(() => refreshSet(false), 1000);
  }, [refresh])

  const edit = (data: AutomationRuleItem) => {
    selectedItemSet(data);
    editDrawerSet(true);
  }

  /** Tagify lists come back as { title, value } — turn the stored ids back into titles. */
  const titlesOf = (source: any[], ids?: string[]) =>
    (ids ?? []).map(id => source.find(s => s.value === id)?.title).filter(t => !!t) as string[];

  const NameItem = (data: AutomationRuleItem) => (
    <div>
      <FlexRow>
        <div className="fw-bold">
          {data.name}
        </div>
        <a href="#" className="ms-2"
          onClick={e => {
            e.preventDefault();
            edit(data);
          }}>
          <Icon icon="edit" size={12} />
        </a>
      </FlexRow>
      {
        data.description &&
        <div className="fs-8 text-gray-600 mt-1 w-250px limited-text">
          {data.description}
        </div>
      }
    </div>
  )

  const EventItem = (data: AutomationRuleItem) => {

    const label = automationEventLabel(data.event);

    // The event's own parameter, spelled out under it — a rule reads as one sentence.
    const statuses = label?.param === "statuses" ? titlesOf(jobStatuses, data.eventJobStatusIds) : [];
    const minutes = (label?.param === "minutesAfterAccept" || label?.param === "minutesBeforeAppt")
      ? data.eventMinutes
      : undefined;

    return (
      <div className="w-250px">
        <FlexRow>
          <Icon icon={label?.icon ?? "bolt"} className="text-gray-600 me-2" size={12} />
          <div className="fs-8">
            {label?.name ?? "—"}
          </div>
        </FlexRow>
        {
          label?.param === "statuses" &&
          <div className="fs-8 text-gray-600 mt-1 ms-5">
            → {statuses.length > 0 ? statuses.join(", ") : "Any status"}
          </div>
        }
        {
          minutes !== undefined &&
          <div className="fs-8 text-gray-600 mt-1 ms-5">
            {label?.param === "minutesAfterAccept"
              ? `${minutes} min after accepting`
              : `${minutes} min before`}
          </div>
        }
      </div>
    )
  }

  const ActionItem = (data: AutomationRuleItem) => {
    const label = automationActionLabel(data.action);
    const targetStatus = data.action === AutomationActions.ChangeJobStatus
      ? jobStatuses.find(s => s.value === data.targetJobStatusId)?.title
      : undefined;

    return (
      <div className="w-250px">
        <FlexRow>
          <Icon icon={label?.icon ?? "wand-magic-sparkles"} className="text-gray-600 me-2" size={12} />
          <div className="fs-8">
            {label?.name ?? "—"}
          </div>
        </FlexRow>
        {
          targetStatus &&
          <div className="fs-8 text-gray-600 mt-1 ms-5">
            → {targetStatus}
          </div>
        }
        {
          data.messageText &&
          <div className="fs-8 text-gray-600 mt-1 ms-5 limited-text">
            {data.messageText}
          </div>
        }
      </div>
    )
  }

  const ConditionsItem = (data: AutomationRuleItem) => {

    const groups: { label: string, values: string[] }[] = [
      { label: "Types", values: titlesOf(jobTypes, data.jobTypeIds) },
      { label: "Sources", values: titlesOf(jobSources, data.jobSourceIds) },
      { label: "Techs", values: titlesOf(techs, data.techIds) },
      { label: "Metros", values: titlesOf(metros, data.metroIds) },
    ].filter(g => g.values.length > 0);

    if (groups.length === 0) return (
      <div className="fs-8 text-gray-500">
        Any job
      </div>
    )

    return (
      <div className="w-250px">
        {
          groups.map(g =>
            <div key={g.label} className="fs-8 mb-1">
              <span className="text-gray-600 me-1">{g.label}:</span>
              <span>{g.values.join(", ")}</span>
            </div>
          )
        }
      </div>
    )
  }

  const CreatedItem = (data: AutomationRuleItem) => (
    <div>
      <div className="d-flex align-items-center">
        {toPrettyDate(data.createdAt)}
      </div>
      {
        data.creatorUser &&
        <FlexRow className="fs-8 text-gray-600 mt-1">
          <Icon icon="user" className="me-2" size={10} type="solid" />
          <div className="fs-8">
            By: {data.creatorUser.fullName}
          </div>
        </FlexRow>
      }
    </div>
  )

  return (
    <>
      <DataTable
        objectName="Automation Rule"
        apiRoute={API_ROUTES.AutomationRules}
        filteringProps={filteringProps}
        sortingProps={sortingProps}
        addCustom={() => createDrawerSet(true)}
        edit editCustom={edit}
        delete
        refresh={refresh}>
        <DataTableField title="" width={50}>
          <DataTableActiveToggle route={API_ROUTES.AutomationRules} />
        </DataTableField>
        <DataTableField title="Rule">
          <NameItem />
        </DataTableField>
        <DataTableField title="When">
          <EventItem />
        </DataTableField>
        <DataTableField title="Then">
          <ActionItem />
        </DataTableField>
        <DataTableField title="Only if">
          <ConditionsItem />
        </DataTableField>
        <DataTableField title="Created">
          <CreatedItem />
        </DataTableField>
      </DataTable>

      {
        createDrawer &&
        <AutomationRuleForm
          onClose={() => createDrawerSet(false)}
          onSave={() => refreshSet(true)} />
      }

      {
        editDrawer && selectedItem &&
        <AutomationRuleForm model={selectedItem}
          onClose={() => {
            editDrawerSet(false);
            selectedItemSet(undefined);
          }}
          onSave={() => refreshSet(true)} />
      }
    </>
  );
}
